import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "package-editor-container app-page app-page--fill" }
const _hoisted_2 = { class: "list-header" }
const _hoisted_3 = { class: "package-list-wrapper" }
const _hoisted_4 = {
  class: "right-panel",
  "element-loading-text": "加载中..."
}
const _hoisted_5 = {
  key: 0,
  class: "edit-area"
}
const _hoisted_6 = { class: "edit-mode-control" }
const _hoisted_7 = { class: "package-name-row" }
const _hoisted_8 = {
  key: 0,
  class: "action-buttons"
}
const _hoisted_9 = { class: "project-table-container" }
const _hoisted_10 = {
  key: 1,
  class: "empty-state"
}

import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import { Plus, Refresh } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DynamicTable from '@/components/DynamicTable.vue';
import { ParameterSettings } from '@/network/index';
import { useButtonPermission } from '@/utils/buttonPermission';

interface PackageFormData {
  id?: number | string;
  name: string;
}

// 左侧面板拖拽调整宽度相关
const minPanelWidth = 200;


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { can } = useButtonPermission('/ParameterSettings/TestItemPackage/index.vue');

const mainContentRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const leftPanelWidth = ref<number>(300);
const isResizing = ref<boolean>(false);
const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResize = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return;
  const containerRect = mainContentRef.value.getBoundingClientRect();
  const maxWidth = containerRect.width / 2;
  let newWidth = e.clientX - containerRect.left;
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth));
  leftPanelWidth.value = newWidth;
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

onUnmounted(() => {
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
});

// 左侧检测项目包列表状态
const activePackageIndex = ref<number>(-1);
const packageList = ref<any[]>([]);
const packageTableModel = ref<Record<string, string> | undefined>(undefined);

const listLoading = ref<boolean>(false);
const detailLoading = ref<boolean>(false);
const saveLoading = ref<boolean>(false);

const filterParams = ref<any[]>([]);

// 分页相关状态（左侧列表使用紧凑布局）
const packagePagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],
  layout: 'total, prev, pager, next', // 移除 sizes 和 jumper，使布局更紧凑
  small: true
});

// 当前编辑/查看的检测项目包
const currentPackage = ref<PackageFormData | null>(null);
const packageFormRef = ref<any>(null);

// 编辑模式状态
const isEditMode = ref<boolean>(false);

// DynamicTable 引用
const projectTableRef = ref<any>(null);

const packageFormRules = {
  name: [
    { required: true, message: '请输入原始记录包名称', trigger: 'blur' }
  ]
};

const packageListData = computed(() => {
  return packageList.value;
});

const activePackageRowKey = computed(() => {
  if (activePackageIndex.value < 0 || activePackageIndex.value >= packageList.value.length) return null;
  const item = packageList.value[activePackageIndex.value];
  return item?.Id ?? item?.id ?? null;
});

// 右侧检测项目列表（构成检测项目包的项目）
const projectListData = ref<any[]>([]);
const projectTableModel = ref<Record<string, string> | undefined>(undefined);
const projectListLoading = ref<boolean>(false);
const projectFilterParams = ref<any[]>([]);
const selectedProjectIds = ref<any[]>([]);
// 保存包内项目的完整数据（通过ID查询获取，用于查看模式显示）
const packageProjectsFullData = ref<any[]>([]);

// 右侧检测项目列表分页
const projectPagination = ref({
  show: true,
  page: 1,
  pageSize: 10,
  total: 0,
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
});

// 根据选中 ID 计算默认选中行数据，供 DynamicTable 使用
const selectedProjectsData = computed(() => {
  if (!Array.isArray(selectedProjectIds.value) || selectedProjectIds.value.length === 0) {
    return [];
  }
  
  if (projectListData.value.length === 0) {
    return [];
  }
  
  const result = projectListData.value.filter(item => {
    const itemId = item.Id || item.id;
    if (!itemId) {
      return false;
    }
    
    // 确保类型一致性，都转换为字符串进行比较
    const itemIdStr = String(itemId);
    const isSelected = selectedProjectIds.value.some(selectedId => String(selectedId) === itemIdStr);
    
    return isSelected;
  });
  
  return result;
});

// 根据编辑模式计算要显示的检测项目数据
const displayProjectListData = computed(() => {
  if (isEditMode.value) {
    // 编辑模式：显示完整的检测项目列表（分页数据）
    return projectListData.value;
  } else {
    // 查看模式：直接返回包内项目数据，el-table 会处理筛选和排序
    return packageProjectsFullData.value;
  }
});

// 加载检测项目包列表
const loadPackageList = async () => {
  try {
    listLoading.value = true;
    // 构建分页参数
    const paginationData = {
      page: packagePagination.value.page,
      intPageSize: packagePagination.value.pageSize
    };
    const res = await ParameterSettings.detectionPackageListIndex(paginationData, filterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      packageList.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      packagePagination.value.total = res.response.dataCount || res.response.total || res.response.count || 0;
      if (res.response.model && typeof res.response.model === 'object') {
        packageTableModel.value = res.response.model;
      } else {
        packageTableModel.value = undefined;
      }
    } else {
      packageList.value = [];
      packageTableModel.value = undefined;
      packagePagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载检测项目包列表失败:', error);
    packageList.value = [];
  } finally {
    listLoading.value = false;
  }
};

// 左侧列表筛选
const handleFilter = (filters: any[]) => {
  filterParams.value = filters;
  packagePagination.value.page = 1; // 筛选时重置到第一页
  loadPackageList();
};

// 分页器事件处理
const handlePackagePageChange = (page: number) => {
  packagePagination.value.page = page;
  loadPackageList();
};

const handlePackagePageSizeChange = (pageSize: number) => {
  packagePagination.value.pageSize = pageSize;
  packagePagination.value.page = 1; // 改变页大小时重置到第一页
  loadPackageList();
};

// 辅助：根据行找到在列表中的索引
const findPackageIndex = (row: any): number => {
  return packageList.value.findIndex((item: any) => {
    if (row.Id !== undefined && item.Id !== undefined) return item.Id === row.Id;
    if (row.id !== undefined && item.id !== undefined) return item.id === row.id;
    return item === row;
  });
};

// 辅助：根据 ID 或名称查找列表项
const findPackageItem = (targetId?: number | string, targetName?: string): any => {
  if (targetId) {
    const targetIdText = String(targetId);
    const foundById = packageList.value.find((item: any) => String(item.Id ?? item.id ?? '') === targetIdText);
    if (foundById) return foundById;
  }
  if (targetName) {
    return packageList.value.find((item: any) => item.名称 === targetName || item.name === targetName);
  }
  return null;
};

// 选择左侧某个检测项目包，加载详情
const handlePackageSelect = async (row: any) => {
  const index = findPackageIndex(row);
  if (index < 0) return;

  activePackageIndex.value = index;
  const item = packageList.value[index];
  const itemId = item.Id || item.id;
  if (!itemId) return;

  try {
    detailLoading.value = true;
    const res = await ParameterSettings.detectionPackageById({ id: itemId });

    if (res && res.status === 200 && res.success && res.response) {
      const data = res.response;

      const projectIds: any[] = [];
      // 兼容两种字段名：检测项目集合 和 检测项目列表
      const projectList = data.检测项目集合 || data.检测项目列表;
      
      // 保存包内项目的完整数据（用于查看模式显示）
      if (Array.isArray(projectList)) {
        packageProjectsFullData.value = projectList;
        projectList.forEach((project: any) => {
          const pid = project.Id || project.id;
          if (pid) {
            projectIds.push(pid);
          }
        });
      } else {
        packageProjectsFullData.value = [];
      }

      currentPackage.value = {
        id: data.id || data.Id,
        name: data.name || data.名称 || ''
      };
      selectedProjectIds.value = projectIds;

      // 默认进入查看模式
      isEditMode.value = false;

      // 使用 nextTick 确保 DOM 更新后再处理选中状态
      await nextTick();

      if (packageFormRef.value) {
        packageFormRef.value.clearValidate?.();
      }
    } else {
      currentPackage.value = null;
      selectedProjectIds.value = [];
      isEditMode.value = false; // 确保进入查看模式
      ElMessage.error('数据加载失败');
    }
  } catch (error) {
    console.error('加载检测项目包详情失败:', error);
    currentPackage.value = null;
    selectedProjectIds.value = [];
    isEditMode.value = false; // 确保进入查看模式
    ElMessage.error('数据加载失败');
  } finally {
    detailLoading.value = false;
  }
};

// DynamicTable 编辑按钮 -> 查看/编辑
const handlePackageEdit = (row: any) => {
  handlePackageSelect(row);
};

// 删除检测项目包
const deletePackage = async (index: number) => {
  const item = packageList.value[index];
  if (!item) return;

  const itemId = item.Id || item.id;

  // 无 ID 的情况（理论上很少出现），直接删除本地项
  if (!itemId) {
    ElMessageBox.confirm('确定要删除这个检测项目包吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(() => {
        packageList.value.splice(index, 1);
        if (activePackageIndex.value === index) {
          activePackageIndex.value = -1;
          currentPackage.value = null;
          selectedProjectIds.value = [];
        }
        ElMessage.success('删除成功');
      })
      .catch(() => { /* cancelled */ });
    return;
  }

  ElMessageBox.confirm('确定要删除这个检测项目包吗？删除后无法恢复', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      try {
        const res = await ParameterSettings.detectionPackageDelete({ id: itemId });

        if (res && res.status === 200 && res.success) {
          const isDeletingActive = activePackageIndex.value === index;
          const activeItem = activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
            ? packageList.value[activePackageIndex.value]
            : null;
          const activeItemId = activeItem?.Id ?? activeItem?.id;

          if (isDeletingActive) {
            activePackageIndex.value = -1;
            currentPackage.value = null;
            selectedProjectIds.value = [];
            packageProjectsFullData.value = [];
            isEditMode.value = false;
          } else if (activePackageIndex.value > index) {
            activePackageIndex.value--;
          }

          if (packageList.value.length === 1 && packagePagination.value.page > 1) {
            packagePagination.value.page--;
          }
          await loadPackageList();
          if (!isDeletingActive && activeItemId) {
            const foundItem = findPackageItem(activeItemId);
            if (foundItem) {
              activePackageIndex.value = findPackageIndex(foundItem);
            } else {
              activePackageIndex.value = -1;
              currentPackage.value = null;
              selectedProjectIds.value = [];
              packageProjectsFullData.value = [];
              isEditMode.value = false;
            }
          }
          ElMessage.success('删除成功');
        } else {
          ElMessage.error('删除失败');
        }
      } catch (error) {
        console.error('删除检测项目包失败:', error);
        ElMessage.error('删除失败');
      }
    })
    .catch(() => { /* cancelled */ });
};

const handlePackageDelete = (row: any) => {
  const index = findPackageIndex(row);
  if (index >= 0) {
    deletePackage(index);
  }
};

// 新增检测项目包：只在右侧创建一个空包，保存后再刷新左侧列表
const addNewPackage = async () => {
  currentPackage.value = {
    name: ''
  };
  selectedProjectIds.value = [];
  packageProjectsFullData.value = []; // 清空包内项目完整数据
  activePackageIndex.value = -1;
  // 新增时进入编辑模式
  isEditMode.value = true;
  if (packageFormRef.value) {
    packageFormRef.value.resetFields?.();
  }
  
  // 新增时加载检测项目列表数据
  if (projectListData.value.length === 0) {
    await loadProjectList();
  }
};

// 重置当前包：新增时清空，已有时重新加载详情
const resetCurrentPackage = async () => {
  if (!currentPackage.value) return;

  if (currentPackage.value.id) {
    const currentItem =
      activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
        ? packageList.value[activePackageIndex.value]
        : null;
    if (currentItem) {
      await handlePackageSelect(currentItem);
    }
  } else {
    currentPackage.value.name = '';
    selectedProjectIds.value = [];
    packageProjectsFullData.value = []; // 清空包内项目完整数据
    if (packageFormRef.value) {
      packageFormRef.value.resetFields?.();
    }
  }
};

// 加载检测项目列表（右侧 DynamicTable）
const loadProjectList = async () => {
  try {
    projectListLoading.value = true;
    // 构建分页参数
    const paginationData = {
      page: projectPagination.value.page,
      intPageSize: projectPagination.value.pageSize
    };
    const res = await ParameterSettings.parameterSettingsListIndex(paginationData, projectFilterParams.value);

    if (res && res.status === 200 && res.success && res.response && res.response.data) {
      projectListData.value = Array.isArray(res.response.data) ? res.response.data : [];
      // 更新总数 - 使用dataCount字段
      const total = res.response.dataCount || res.response.total || res.response.count || 0;
      projectPagination.value.total = total;
      if (res.response.model && typeof res.response.model === 'object') {
        projectTableModel.value = res.response.model;
      } else {
        projectTableModel.value = undefined;
      }
    } else {
      projectListData.value = [];
      projectTableModel.value = undefined;
      projectPagination.value.total = 0;
    }
  } catch (error) {
    console.error('加载检测项目列表失败:', error);
    projectListData.value = [];
    ElMessage.error('加载检测项目列表失败');
  } finally {
    projectListLoading.value = false;
  }
};

// 处理检测项目筛选（仅编辑模式）
const handleProjectFilter = async (filters: any[], savedSelection?: any[]) => {
  projectFilterParams.value = filters;
  projectPagination.value.page = 1;
  await loadProjectList();
};

// 处理检测项目排序（仅编辑模式）
const handleProjectSort = (column: string, type: number) => {
  // 编辑模式的排序逻辑（如果后端支持）
  console.log('编辑模式排序:', column, type);
};


// 检测项目列表分页器事件处理
const handleProjectPageChange = (page: number) => {
  // 只在编辑模式下处理分页，查看模式下不需要分页
  if (!isEditMode.value) {
    return;
  }

  projectPagination.value.page = page;
  loadProjectList();
  
  // selectedProjectIds 保持不变，DynamicTable 会通过 defaultSelection 自动恢复勾选
};

const handleProjectPageSizeChange = (pageSize: number) => {
  // 只在编辑模式下处理分页，查看模式下不需要分页
  if (!isEditMode.value) {
    return;
  }

  projectPagination.value.pageSize = pageSize;
  projectPagination.value.page = 1; // 改变页大小时重置到第一页
  loadProjectList();
  
  // selectedProjectIds 保持不变，DynamicTable 会通过 defaultSelection 自动恢复勾选
};

// 处理检测项目勾选变化
const handleProjectSelectionChange = (selection: any[]) => {
  // 只在编辑模式下才处理勾选变化，避免查看模式下被意外清空
  if (!isEditMode.value) {
    return;
  }
  
  // 获取当前表格中显示的所有项目ID
  const currentDisplayIds = projectListData.value.map(item => String(item.Id || item.id));
  
  // 获取当前选中的项目ID
  const currentSelectedIds = selection.map(item => String(item.Id || item.id));
  
  // 如果 selection 是空数组，但 selectedProjectIds 有值，
  // 且当前页包含应该被选中的项目，说明是 DynamicTable 初始化导致的误触发，跳过处理
  if (currentSelectedIds.length === 0 && selectedProjectIds.value.length > 0) {
    const shouldBeSelectedInCurrentPage = selectedProjectIds.value.filter(id =>
      currentDisplayIds.includes(String(id))
    );
    if (shouldBeSelectedInCurrentPage.length > 0) {
      // 这是 DynamicTable 初始化时的误触发，不应该清空 selectedProjectIds
      return;
    }
  }
  
  // 保留不在当前显示列表中的已选项目ID（被筛选掉的项目或其他页的项目）
  const hiddenSelectedIds = selectedProjectIds.value.filter(id => 
    !currentDisplayIds.includes(String(id))
  );
  
  // 合并当前选中的ID和被筛选掉的ID
  const allSelectedIds = [...currentSelectedIds, ...hiddenSelectedIds];
  
  selectedProjectIds.value = allSelectedIds;
};

// 保存检测项目包
const savePackageData = async () => {
  if (!currentPackage.value) {
    ElMessage.warning('请先创建或选择检测项目包');
    return;
  }

  if (packageFormRef.value) {
    await packageFormRef.value.validate(async (valid: boolean) => {
      if (!valid) {
        ElMessage.warning('请填写必填项');
        return;
      }

      if (!selectedProjectIds.value || selectedProjectIds.value.length === 0) {
        ElMessage.warning('请至少选择一个检测项目');
        return;
      }

      const pkg = currentPackage.value;
      if (!pkg) {
        ElMessage.warning('请先创建或选择检测项目包');
        return;
      }

      const currentId = pkg.id;
      const isNew = !currentId;

      const submitData: any = {
        名称: pkg.name,
        检测项目集合: selectedProjectIds.value.map((id: any) => ({ Id: id })),
        ISDELETED: false,
        Id: currentId || ''
      };

      try {
        saveLoading.value = true;
        const res = await ParameterSettings.detectionPackageSave(submitData);

        if (res && res.status === 200 && res.success) {
          if (isNew) {
            ElMessage.success('新增成功');
            // 获取返回的ID（后端返回格式：response.ID）
            const newId = res.response?.ID || res.response?.Id || res.response?.id;
            const targetName = pkg.name;
            
            packagePagination.value.page = 1;
            await loadPackageList();
            // 刷新列表后，重新选中当前项（优先通过ID匹配，其次通过名称匹配）
            const foundItem = newId ? findPackageItem(newId) : findPackageItem(undefined, targetName);
            if (foundItem) {
              await handlePackageSelect(foundItem);
              // 保存成功后进入查看模式
              isEditMode.value = false;
            } else if (newId) {
              const detailRes = await ParameterSettings.detectionPackageById({ id: newId });
              if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
                const data = detailRes.response;
                const projectIds: any[] = [];
                const projectList = data.检测项目集合 || data.检测项目列表;
                packageProjectsFullData.value = Array.isArray(projectList) ? projectList : [];
                packageProjectsFullData.value.forEach((project: any) => {
                  const pid = project.Id || project.id;
                  if (pid) projectIds.push(pid);
                });
                currentPackage.value = {
                  id: data.id || data.Id,
                  name: data.name || data.名称 || ''
                };
                selectedProjectIds.value = projectIds;
                activePackageIndex.value = -1;
                isEditMode.value = false;
              }
            }
          } else {
            ElMessage.success('更新成功');
            const currentItem =
              activePackageIndex.value >= 0 && activePackageIndex.value < packageList.value.length
                ? packageList.value[activePackageIndex.value]
                : null;
            if (currentItem) {
              currentItem.名称 = pkg.name;
              currentItem.name = pkg.name;
            }
            
            // 更新成功后，重新获取包的详细信息以确保数据是最新的
            if (pkg.id) {
              try {
                const detailRes = await ParameterSettings.detectionPackageById({ id: pkg.id });
                if (detailRes && detailRes.status === 200 && detailRes.success && detailRes.response) {
                  const data = detailRes.response;
                  const projectIds: any[] = [];
                  const projectList = data.检测项目集合 || data.检测项目列表;
                  
                  // 更新包内项目的完整数据
                  if (Array.isArray(projectList)) {
                    packageProjectsFullData.value = projectList;
                    projectList.forEach((project: any) => {
                      const pid = project.Id || project.id;
                      if (pid) {
                        projectIds.push(pid);
                      }
                    });
                  } else {
                    packageProjectsFullData.value = [];
                  }
                  
                  selectedProjectIds.value = projectIds;
                }
              } catch (error) {
                console.error('更新后重新加载详情失败:', error);
              }
            }
            
            // 保存成功后进入查看模式
            isEditMode.value = false;
          }
        } else {
          ElMessage.error(isNew ? '新增失败' : '更新失败');
        }
      } catch (error) {
        console.error('保存检测项目包失败:', error);
        ElMessage.error(isNew ? '新增失败' : '更新失败');
      } finally {
        saveLoading.value = false;
      }
    });
  }
};

// 进入编辑模式
const enterEditMode = async () => {
  // 在切换编辑模式之前，保存当前的选择状态
  const currentSelectedProjects = selectedProjectsData.value;
  
  isEditMode.value = true;
  
  // 进入编辑模式时，加载完整的检测项目列表
  await loadProjectList();
  
  // 进入编辑模式后，同步当前选择状态到DynamicTable
  await nextTick();
  
  if (projectTableRef.value && currentSelectedProjects.length > 0) {
    // 初始化DynamicTable的currentSelection状态
    await projectTableRef.value.restoreSelection(currentSelectedProjects);
  }
};

// 退出编辑模式
const exitEditMode = async () => {
  isEditMode.value = false;
  
  // 重新加载当前包的详情，恢复到查看状态
  if (currentPackage.value && currentPackage.value.id) {
    const currentItem = packageList.value.find(item => 
      (item.Id || item.id) === (currentPackage.value?.id)
    );
    if (currentItem) {
      await handlePackageSelect(currentItem);
    }
  }
};


onMounted(() => {
  loadPackageList();
  // 不需要在初始化时加载项目列表，只在编辑模式或需要model时加载
});

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_card = _resolveComponent("el-card")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_card, null, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: "main-content",
          ref_key: "mainContentRef",
          ref: mainContentRef
        }, [
          _createElementVNode("div", {
            class: "left-panel",
            ref_key: "leftPanelRef",
            ref: leftPanelRef,
            style: _normalizeStyle({ width: leftPanelWidth.value + 'px' })
          }, [
            _createElementVNode("div", _hoisted_2, [
              (_unref(can)('新增'))
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 0,
                    type: "primary",
                    onClick: addNewPackage
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Plus))
                        ]),
                        _: 1
                      }),
                      _cache[1] || (_cache[1] = _createTextVNode(" 新增 "))
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_el_button, {
                type: "primary",
                circle: "",
                size: "small",
                class: "app-refresh-btn",
                onClick: loadPackageList,
                loading: listLoading.value,
                icon: _unref(Refresh),
                style: {"margin-left":"auto"},
                title: "刷新"
              }, null, 8, ["loading", "icon"])
            ]),
            _createElementVNode("div", _hoisted_3, [
              _createVNode(DynamicTable, {
                data: packageListData.value,
                model: packageTableModel.value,
                loading: listLoading.value,
                "show-column-selector": false,
                "show-export": false,
                "show-edit-button": _unref(can)('查看'),
                "show-delete-button": _unref(can)('删除'),
                pagination: packagePagination.value,
                "active-row-key": activePackageRowKey.value,
                onEdit: handlePackageEdit,
                onDelete: handlePackageDelete,
                onFilter: handleFilter,
                onPageChange: handlePackagePageChange,
                onPageSizeChange: handlePackagePageSizeChange
              }, null, 8, ["data", "model", "loading", "show-edit-button", "show-delete-button", "pagination", "active-row-key"])
            ])
          ], 4),
          _createElementVNode("div", {
            class: "resize-handle",
            onMousedown: startResize
          }, null, 32),
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_4, [
            (currentPackage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    (!isEditMode.value && _unref(can)('编辑'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          type: "primary",
                          onClick: enterEditMode
                        }, {
                          default: _withCtx(() => _cache[2] || (_cache[2] = [
                            _createTextVNode(" 编辑 ")
                          ])),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isEditMode.value)
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 1,
                          onClick: exitEditMode
                        }, {
                          default: _withCtx(() => _cache[3] || (_cache[3] = [
                            _createTextVNode(" 取消编辑 ")
                          ])),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _createVNode(_component_el_form, {
                    ref_key: "packageFormRef",
                    ref: packageFormRef,
                    model: currentPackage.value,
                    rules: packageFormRules,
                    "label-width": "150px",
                    class: "form-content"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_7, [
                        _createVNode(_component_el_form_item, {
                          class: "package-name-item",
                          label: "原始记录包名称",
                          prop: "name"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_input, {
                              modelValue: currentPackage.value.name,
                              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentPackage.value.name) = $event)),
                              placeholder: "请输入原始记录包名称",
                              readonly: !isEditMode.value
                            }, null, 8, ["modelValue", "readonly"])
                          ]),
                          _: 1
                        }),
                        (isEditMode.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              (_unref(can)('保存'))
                                ? (_openBlock(), _createBlock(_component_el_button, {
                                    key: 0,
                                    type: "primary",
                                    onClick: savePackageData,
                                    loading: saveLoading.value
                                  }, {
                                    default: _withCtx(() => _cache[4] || (_cache[4] = [
                                      _createTextVNode("保存数据")
                                    ])),
                                    _: 1
                                  }, 8, ["loading"]))
                                : _createCommentVNode("", true),
                              _createVNode(_component_el_button, {
                                onClick: resetCurrentPackage,
                                disabled: saveLoading.value
                              }, {
                                default: _withCtx(() => _cache[5] || (_cache[5] = [
                                  _createTextVNode("重置")
                                ])),
                                _: 1
                              }, 8, ["disabled"])
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        (!isEditMode.value)
                          ? (_openBlock(), _createBlock(_component_el_table, {
                              key: 0,
                              data: displayProjectListData.value,
                              loading: projectListLoading.value,
                              stripe: "",
                              border: "",
                              style: {"width":"100%"},
                              height: "100%"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_el_table_column, {
                                  prop: "名称",
                                  label: "名称",
                                  "min-width": "150"
                                }),
                                _createVNode(_component_el_table_column, {
                                  prop: "数量",
                                  label: "数量",
                                  width: "100"
                                }),
                                _createVNode(_component_el_table_column, {
                                  prop: "验证数量",
                                  label: "验证数量",
                                  width: "120"
                                }, {
                                  default: _withCtx(({ row }) => [
                                    _createTextVNode(_toDisplayString(row.验证数量 ? '是' : '否'), 1)
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_el_table_column, {
                                  prop: "转跳方向",
                                  label: "转跳方向",
                                  width: "120"
                                }, {
                                  default: _withCtx(({ row }) => [
                                    _createTextVNode(_toDisplayString(row.转跳方向 ? '是' : '否'), 1)
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_el_table_column, {
                                  prop: "Id",
                                  label: "ID",
                                  width: "150"
                                })
                              ]),
                              _: 1
                            }, 8, ["data", "loading"]))
                          : (_openBlock(), _createBlock(DynamicTable, {
                              key: 1,
                              ref_key: "projectTableRef",
                              ref: projectTableRef,
                              data: displayProjectListData.value,
                              model: projectTableModel.value,
                              loading: projectListLoading.value,
                              "show-column-selector": true,
                              "show-export": false,
                              "show-edit-button": false,
                              "show-delete-button": false,
                              showSelection: true,
                              defaultSelection: selectedProjectsData.value,
                              pagination: projectPagination.value,
                              onFilter: handleProjectFilter,
                              onSort: handleProjectSort,
                              onSelectionChange: handleProjectSelectionChange,
                              onPageChange: handleProjectPageChange,
                              onPageSizeChange: handleProjectPageSizeChange
                            }, null, 8, ["data", "model", "loading", "defaultSelection", "pagination"]))
                      ])
                    ]),
                    _: 1
                  }, 8, ["model"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createVNode(_component_el_empty, { description: "请先创建或选择一个检测项目包" })
                ]))
          ])), [
            [_directive_loading, detailLoading.value]
          ])
        ], 512)
      ]),
      _: 1
    })
  ]))
}
}

})